import { of, fromEvent, Observable, ReplaySubject, Subscription } from 'rxjs';

import { takeUntil, filter, share, mergeMap, repeat, delay, take, mergeWith } from 'rxjs/operators';
import {
	Component, OnInit, OnDestroy, AfterViewInit, ElementRef,
	Input, Output, EventEmitter, SimpleChanges, ViewChild, HostListener, OnChanges
} from '@angular/core';

import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import 'hammerjs';

@Component({
	selector: 'bb-canvas',
	templateUrl: './bb-canvas.component.html',
	styleUrls: ['./bb-canvas.component.scss']
})

export class BbCanvasComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
	zoomLevels: number[] = [];
	/* eslint-disable @typescript-eslint/member-ordering */
	@Input() initialZoom: number = (this.zoomLevels.length - 1);
	@Input() zoomToFit = false;
	@Input() outline = true;
	@Input() dragDisabled = false;
	@Output() zoomChange: EventEmitter<number> = new EventEmitter<number>();
	@Output() initialized: EventEmitter<boolean> = new EventEmitter<boolean>();

	@ViewChild('outlineEl') outlineEl: ElementRef;
	@ViewChild('outlineWindowEl') outlineWindowEl: ElementRef;
	@ViewChild('controlsEl') controlsEl: ElementRef;

	numZoomLevels = 50;
	isVertical = false;

	// dragHotkey: number = 32; // space
	isGrabbingCanvas = false;
	isGrabbingOutline = false;
	isScrolling = true;
	scrollTotal = 0;
	zoom: number = this.initialZoom;
	zoomToFitInProgress = true; // we start at 'true' initially so we can hide element from the beginning
	contentFits: boolean;

	el: HTMLElement;
	canvas: Element;
	canvasContainer: Element;

	keyDownObs: Observable<Event>;
	keyUpObs: Observable<Event>;
	mouseDownObs: Observable<Event>;
	mouseUpObs: Observable<Event>;

	outlineWindowMouseDownObs: Observable<Event>;
	outlineMouseMoveObs: Observable<Event>;

	outlineWindowDragStartObs: Observable<Event>;
	outlineWindowDragObs: Observable<any>;
	outlineWindowDragStopObs: Observable<Event>;
	outlineWindowDragStartEvent: MouseEvent;
	outlineWindowDragStartLeft: number;
	outlineWindowDragStartTop: number;

	mouseMoveObs: Observable<Event>;
	scrollObs: Observable<Event>;
	windowResize: Observable<Event>;

	canvasDragStartObs: Observable<MouseEvent>;
	canvasDragStopObs: Observable<MouseEvent>;
	canvasDragObs: Observable<MouseEvent>;
	canvasPositionObs: ReplaySubject<{ left: number; top: number; zoom: number }>;

	canvasDragPosition: { x: number; y: number };
	canvasPosition: { x: number; y: number };
	canvasSize: ClientRect;
	canvasContainerSize: ClientRect;
	canvasTopInitial: number;

	outlineInitialized = false;

	outlineWidth = '0px';
	outlineHeight = '0px';

	outlineWindowWidth = '0px';
	outlineWindowHeight = '0px';
	outlineWindowLeft = '0px';
	outlineWindowTop = '0px';
	outlineMaxWidth = 180;
	outlineMaxHeight = 180;

	isPinching = false;
	initialPinchScale: number;
	initialPinchZoom: number;
	pinchCenter: { x: number; y: number };
	panStartPosition: { x: number; y: number };
	taps = 0;
	keysPressed: any = {};

	subscriptions = new Subscription();

	constructor(private _eref: ElementRef, private hotkeysService: HotkeysService) {
		// Constructor
		this.el = this._eref.nativeElement;
		this.canvasPositionObs = new ReplaySubject(1);

		for (let i = this.numZoomLevels; i > 0; i--) {
			this.zoomLevels.push(100 * Math.pow(0.95, i));
		}
	}

	ngOnInit() {
		this.canvas = this.el.getElementsByClassName('canvas')[0];
		this.canvasContainer = this.el.getElementsByClassName('canvas-container')[0];

		this.canvasContainerSize = this.canvasContainer.getBoundingClientRect();
		this.canvasPosition = { x: 0, y: 0 };

		// Much observable, so reactive, wow
		this.windowResize = fromEvent(window, 'resize');
		// Create some observables we can use to get mouse / keyboard events
		this.keyUpObs = fromEvent(window, 'keyup');
		this.keyDownObs = fromEvent(window, 'keydown');
		this.keyDownObs.pipe(
			mergeWith(this.keyUpObs),
			filter((() => {
				// Disable keypress repetitions
				const keysPressed = {};
				return (e: KeyboardEvent) => {
					const k = e.key;
					if (e.type === 'keyup') {
						delete keysPressed[k];
						return false;
					} else {
						this.keysPressed[k] = true;
						return true;
					}
				};
			})()));
		this.mouseDownObs = fromEvent(this.el, 'mousedown');
		this.mouseUpObs = fromEvent(this.el, 'mouseup');
		this.mouseMoveObs = fromEvent(document, 'mousemove');
		this.scrollObs = fromEvent(this.el, 'wheel');

		this.subscriptions.add(this.scrollObs.subscribe((ev: WheelEvent) => this.onCanvasScroll(ev)));
		this.subscriptions.add(this.windowResize.subscribe((ev: UIEvent) => this.onWindowResize(ev)));

		// hotkeys

		this.hotkeysService.add(new Hotkey(['mod+=', 'mod++'], (event: KeyboardEvent): boolean => {
			this.zoomIn();
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+-', (event: KeyboardEvent): boolean => {
			this.zoomOut();
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+0', (event: KeyboardEvent): boolean => {
			this.zoomReset();
			return false;
		}));

	}

	ngAfterViewInit() {

		this.canvasContainerSize = this.canvasContainer.getBoundingClientRect();
		this.canvasSize = this.canvas.getBoundingClientRect();

		if (this.zoomToFit) {

			setTimeout(() => {
				this.setZoomToFit();
			});
		}

		if (this.outline) {
			this.initOutline();
		}

		setTimeout(() => {
			this.initialized.emit(true);
		});
	}

	initOutline() {

		// create observables
		this.outlineWindowMouseDownObs = fromEvent(this.outlineWindowEl.nativeElement, 'mousedown');
		this.outlineMouseMoveObs = fromEvent(this.outlineEl.nativeElement, 'mousemove');

		this.outlineWindowDragStartObs = this.outlineWindowMouseDownObs;

		this.outlineWindowDragObs = this.outlineWindowMouseDownObs
			.pipe(
				mergeMap((mouseDownEvent: MouseEvent) =>
					this.outlineMouseMoveObs.pipe(takeUntil(this.mouseUpObs))
				),
				repeat(),
				share()
			);

		this.outlineWindowDragStopObs = this.outlineWindowDragStartObs
			.pipe(
				mergeMap(
					(mouseMoveEvent: MouseEvent) =>
						this.mouseUpObs
				),
				delay(100),
				take(1),
				repeat(),
				share()
			);


		// now subscribe

		this.subscriptions.add(this.outlineWindowDragStartObs.subscribe((ev: MouseEvent) => {
			this.isGrabbingOutline = true;

			this.outlineWindowDragStartEvent = ev;
			this.outlineWindowDragStartLeft = parseInt(this.outlineWindowLeft, 10);
			this.outlineWindowDragStartTop = parseInt(this.outlineWindowTop, 10);
		}));

		this.subscriptions.add(this.outlineWindowDragObs.subscribe(ev => {

			ev.stopPropagation();

			// move outline window
			const outlineWindowSize = this.outlineWindowEl.nativeElement.getBoundingClientRect();
			const outlineSize = this.outlineEl.nativeElement.getBoundingClientRect();

			const deltaX = (ev.clientX - this.outlineWindowDragStartEvent.clientX);
			let calculatedLeft = this.outlineWindowDragStartLeft + deltaX;
			calculatedLeft = Math.max(0, calculatedLeft);
			this.outlineWindowLeft = Math.min(calculatedLeft, (outlineSize.width - outlineWindowSize.width)) + 'px';

			const deltaY = (ev.clientY - this.outlineWindowDragStartEvent.clientY);
			let calculatedTop = this.outlineWindowDragStartTop + deltaY;
			calculatedTop = Math.max(0, calculatedTop);
			this.outlineWindowTop = Math.min(calculatedTop, (outlineSize.height - outlineWindowSize.height)) + 'px';

			// determine new canvas position, based on the position of the outline window relative to its container
			const outlineWindowLeft = parseInt(this.outlineWindowLeft, 10);
			const outlineWindowTop = parseInt(this.outlineWindowTop, 10);

			const xPercentage = (outlineWindowLeft / outlineSize.width);
			const yPercentage = (outlineWindowTop / outlineSize.height);

			const newPos = {
				x: -xPercentage * this.canvasSize.width,
				y: -yPercentage * this.canvasSize.height,
			};

			this.setCanvasPosition(newPos, true);
		}));

		this.subscriptions.add(this.outlineWindowDragStopObs.subscribe((ev: MouseEvent) => {
			this.isGrabbingOutline = false;
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges) {

		if (!changes.zoomToFit || !changes.zoomToFit.currentValue) {
			this.zoomToFitInProgress = false;
		}

		if (changes.zoomToFit && !changes.zoomToFit.firstChange) {

			if (this.zoomToFit) {
				this.setZoomToFit();
			}
		}
	}

	/* Interaction events */
	onWindowResize(ev: UIEvent) {
		this.canvasContainerSize = this.canvasContainer.getBoundingClientRect();
		this.canvasSize = this.canvas.getBoundingClientRect();
		this.setCanvasPosition(this.canvasPosition);
		this.positionOutline();
	}

	/* Canvas drag */
	setCanvasPosition(position: { x: number; y: number }, needs_zoom_fix = false, usePercent = false): void {

		// make sure we don't overstep bounderies
		this.canvasContainerSize = this.canvasContainer.getBoundingClientRect();
		this.canvasSize = this.canvas.getBoundingClientRect();

		// calculate the original, un-zoomed widths and heights

		const zoomPercentage = (this.zoomLevels[this.zoom] / 100);
		const canvasOriginalHeight = (this.canvasSize.height / zoomPercentage);
		const canvasOriginalWidth = (this.canvasSize.width / zoomPercentage);
		const canvasContainerOriginalHeight = (this.canvasContainerSize.height / zoomPercentage);
		const canvasContainerOriginalWidth = (this.canvasContainerSize.width / zoomPercentage);

		// how much have our elements been resized by applying zoom?
		const canvasContainerHeightResizeAmount = (canvasContainerOriginalHeight - this.canvasContainerSize.height);
		const canvasContainerWidthResizeAmount = (canvasContainerOriginalWidth - this.canvasContainerSize.width);

		let exportPosition = { ...position };

		if (needs_zoom_fix) {
			// fox the supplied canvas position, which was calculated without regards for any zooming we might have applied
			position.x = (position.x / zoomPercentage) - (canvasContainerWidthResizeAmount / 2);
			position.y = (position.y / zoomPercentage) - (canvasContainerHeightResizeAmount / 2);
		}

		const topBoundary = -(canvasContainerHeightResizeAmount / 2);
		const leftBoundary = -(canvasContainerWidthResizeAmount / 2);
		const rightBoundary = -canvasOriginalWidth + this.canvasContainerSize.width + (canvasContainerWidthResizeAmount / 2);
		const bottomBoundary = -canvasOriginalHeight + this.canvasContainerSize.height + (canvasContainerHeightResizeAmount / 2);

		if (usePercent) {
			position.x = Math.max(position.x, 0);
			position.x = Math.min(position.x, 100);
			position.y = Math.max(position.y, 0);
			position.y = Math.min(position.y, 100);
			position.x = leftBoundary + ((position.x / 100) * (rightBoundary - leftBoundary));
			position.y = topBoundary + ((position.y / 100) * (bottomBoundary - topBoundary));
		} else {
			// apply boundaries
			position.x = Math.max(rightBoundary, position.x);
			position.y = Math.max(bottomBoundary, position.y);
			position.x = Math.min(leftBoundary, position.x);
			position.y = Math.min(topBoundary, position.y);
		}

		let originCorrection = 0;

		if (!needs_zoom_fix) {
			exportPosition = position;
			originCorrection = ((1 - zoomPercentage) * this.getCanvasOrigin(position).x);
		}

		this.canvasPositionObs.next({
			left: exportPosition.x + originCorrection,
			top: exportPosition.y,
			zoom: zoomPercentage
		});

		this.canvasPosition = position;
	}

	onCanvasScroll(ev: WheelEvent) {

		this.scrollTotal -= ev.deltaY;

		// no changes in the last x msec? reset
		this.subscriptions.add(of(this.scrollTotal).pipe(delay(100))
			.subscribe((oldTotal: number) => {

				if (oldTotal === this.scrollTotal) {
					this.scrollTotal = 0;
				}
			}));

		const newZoom = (this.zoom + Math.round(this.scrollTotal / 100));

		if (newZoom !== this.zoom) {
			this.setZoom(newZoom);
			this.scrollTotal = 0;
			this.positionOutline();
		}
	}


	/* Zoom functionality */
	setZoom(newZoom: number, skip_positioning: boolean = false) {
		if (this.zoom !== newZoom) {
			if (typeof this.zoomLevels[newZoom] === 'undefined') {
				if (newZoom > this.zoom) {
					// max zoom
					newZoom = this.zoomLevels.length - 1;
				} else {
					// min zoom
					newZoom = 0;
				}
			}
			this.zoom = newZoom;
			this.zoomChange.emit(this.zoom);

			if (!skip_positioning) {
				this.setCanvasPosition(this.canvasPosition);

				// wait for rendering of new position
				setTimeout(() => {
					this.positionOutline();
					this.determineContentFits();
				});
			}
		}
	}

	outlineClicked(event: MouseEvent) {

		if (this.isGrabbingOutline) {
			return;
		}

		const outlineSize = this.outlineEl.nativeElement.getBoundingClientRect();
		const outlineWindowSize = this.outlineWindowEl.nativeElement.getBoundingClientRect();

		const eventX = (event.clientX - outlineSize.left);
		const eventY = (event.clientY - outlineSize.top);

		// we calculate the new x/y of the left/top corner of the outline window
		const percentageX = ((eventX - (outlineWindowSize.width / 2)) / outlineSize.width);
		const percentageY = ((eventY - (outlineWindowSize.height / 2)) / outlineSize.height);

		const zoomPercentage = this.zoomLevels[this.zoom] / 100;

		const newPos = {
			x: -(percentageX * (this.canvasSize.width / zoomPercentage)),
			y: -(percentageY * (this.canvasSize.height / zoomPercentage))
		};

		this.setCanvasPosition(newPos);

		// wait for rendering of new position
		setTimeout(() => {
			this.positionOutline();
		});
	}

	positionOutline() {

		if (!this.outline) {
			return;
		}

		const canvasContainerSize = this.canvasContainer.getBoundingClientRect();
		const canvasSize = this.canvas.getBoundingClientRect();

		if (!canvasSize.width) {
			return;
		}

		// size outline
		let outlineWidth;
		let outlineHeight;

		// determine max width or height
		if (canvasSize.height > canvasSize.width) {
			outlineHeight = this.outlineMaxHeight;
			outlineWidth = Math.round((canvasSize.width / canvasSize.height) * outlineHeight);
			this.isVertical = true;
		} else {
			outlineWidth = this.outlineMaxWidth;
			outlineHeight = Math.round((canvasSize.height / canvasSize.width) * outlineWidth);
			this.isVertical = false;
		}

		this.outlineWidth = outlineWidth.toString() + 'px';
		this.outlineHeight = outlineHeight.toString() + 'px';

		// size outline window
		const outlineWindowWidthPercentage = Math.min(1, (canvasContainerSize.width / canvasSize.width));
		this.outlineWindowWidth = Math.round(outlineWindowWidthPercentage * outlineWidth).toString() + 'px';

		const outlineWindowHeightPercentage = Math.min(1, (canvasContainerSize.height / canvasSize.height));
		this.outlineWindowHeight = Math.round(outlineWindowHeightPercentage * outlineHeight).toString() + 'px';

		// position outline window
		let outlineWindowLeftPercentage = 1 - (canvasSize.right / canvasSize.width);
		outlineWindowLeftPercentage = Math.max(0, outlineWindowLeftPercentage);

		this.outlineWindowLeft = Math.round(outlineWindowLeftPercentage * outlineWidth).toString() + 'px';

		if (typeof this.canvasTopInitial === 'undefined') {
			this.canvasTopInitial = canvasSize.top;
		}

		let outlineWindowTopPercentage = (this.canvasTopInitial - canvasSize.top) / canvasSize.height;
		outlineWindowTopPercentage = Math.max(0, outlineWindowTopPercentage);
		this.outlineWindowTop = Math.round(outlineWindowTopPercentage * outlineHeight) + 'px';
	}

	setZoomToFit() {
		this.zoomToFitInProgress = true;

		const canvasSize = this.canvas.getBoundingClientRect();
		const originalCanvasWidth = canvasSize.width;
		const originalCanvasHeight = canvasSize.height;

		const containerSize = this.canvasContainer.getBoundingClientRect();
		const containerWidth = containerSize.width;
		const containerHeight = containerSize.height;

		for (let zoomLevel = (this.zoomLevels.length - 1); zoomLevel >= 0; zoomLevel--) {

			const percentage = this.zoomLevels[zoomLevel];

			const targetCanvasWidth = ((percentage / 100) * originalCanvasWidth);
			const targetCanvasHeight = ((percentage / 100) * originalCanvasHeight);

			const fits = (targetCanvasWidth < containerWidth && targetCanvasHeight < containerHeight);

			if (fits || zoomLevel === 5) {
				this.initialZoom = zoomLevel;
				this.setZoom(zoomLevel);
				break;
			}
		}

		setTimeout(() => {
			this.positionOutline();
			this.determineContentFits();
			this.zoomToFitInProgress = false;
		});
	}

	determineContentFits() {

		const canvasSize = this.canvas.getBoundingClientRect();
		const containerSize = this.canvasContainer.getBoundingClientRect();

		this.contentFits = (canvasSize.width < containerSize.width && canvasSize.height < containerSize.height);
	}

	zoomIn() {
		const newZoom = Math.ceil((this.zoom + 5) / 5) * 5; // steps of 5
		this.setZoom(newZoom - 1);
	}

	zoomOut() {
		const newZoom = Math.ceil((this.zoom - 5) / 5) * 5; // steps of 5
		this.setZoom(newZoom - 1);
	}

	zoomReset() {
		this.setZoom(this.initialZoom);
		this.setCanvasPosition(this.canvasPosition);
	}



	/* touch listeners */


	// double tab to reset zoom, this should work, but ...
	// we should correctly configure a doubletap event in hammer configuration instead of counting taps ourselves
	// the logic around elements we want to ignore should probable be better
	@HostListener('tap', ['$event'])
	onDoubleTap(event: any) {

		if (this.outlineEl.nativeElement.contains(event.target) ||
			this.controlsEl.nativeElement.contains(event.target)) {
			return;
		}

		this.taps++;

		if (this.taps === 2) {
			event.preventDefault();
			this.zoomReset();
			this.taps = 0;
		}

		setTimeout(() => {
			this.taps = Math.max(0, this.taps - 1);
		}, 200);
	}

	@HostListener('panstart', ['$event'])
	onCanvasDragStart(event: any) {
		if (!this.dragDisabled) {
			this.isGrabbingCanvas = true;
		}
		this.canvasDragPosition = this.canvasPosition;
	}

	@HostListener('pan', ['$event'])
	onCanvasDrag(event: any) {
		if (this.isGrabbingOutline || this.dragDisabled) {
			return;
		}

		const percentage = this.zoomLevels[this.zoom];

		if (this.canvasDragPosition) {
			this.setCanvasPosition({
				x: (event.deltaX * (100 / percentage)) + this.canvasDragPosition.x,
				y: (event.deltaY * (100 / percentage)) + this.canvasDragPosition.y
			});
		}

		this.positionOutline();
	}

	@HostListener('panend', ['$event'])
	onCanvasDragEnd(ev: MouseEvent) {
		this.isGrabbingCanvas = false;
		this.canvasDragPosition = null;
	}

	@HostListener('pinch', ['$event'])
	onPinch(event: any) {

		event.preventDefault();

		if (!this.isPinching) {
			return;
		}

		this.pinchCenter = event.center;

		const scaleDiff = (event.scale - this.initialPinchScale);
		const newZoom = this.initialPinchZoom + Math.round(5 * scaleDiff);
		this.setZoom(newZoom, true);
	}

	@HostListener('pinchstart', ['$event'])
	onPinchStart(event: any) {

		event.preventDefault();

		this.isPinching = true;
		this.initialPinchScale = event.scale;
		this.initialPinchZoom = this.zoom;
	}

	@HostListener('pinchend', ['$event'])
	onPinchEnd(event: any) {

		event.preventDefault();

		this.isPinching = false;
		this.pinchCenter = null;
		this.initialPinchScale = null;
		this.initialPinchZoom = null;
	}



	/* Helpers */
	getCanvasScale(): string {
		const scale = (this.zoomLevels[this.zoom] / 100);
		return 'scale(' + scale + ', ' + scale + ')';
	}

	getCanvasOrigin(position: { x: number; y: number }): { x: number; y: number } {

		const originX = ((this.canvasContainerSize.width / 2) - position.x);
		const originY = ((this.canvasContainerSize.height / 2) - position.y);

		return { x: originX, y: originY };
	}

	getCanvasTransformOrigin(): string {
		const origin = this.getCanvasOrigin(this.canvasPosition);
		return origin.x + 'px ' + origin.y + 'px';
	}
}
